/**
 * Mayhem challenges, the half-bricks (order 536; docs/ACTIVITIES-OFFLINE.md). Ten bricks with a note tied
 * round them lie about the borough; walk over one and a two-minute challenge starts: knock down the Vanes'
 * marked men, smash up their claret motors or send the pavement clutter flying, with the weapon or the car
 * the note leaves you. Score, combo and the three stamps come from `mayhem.ts`; this file plays them.
 *
 * Each challenge is a `MissionDef` handed to the mission system (`registerActivities`), so it gets the
 * intro card, the objective clipping, NICKED / WASTED, the result sheet, the payout through the economy
 * and "start again" for free. The source only offers a challenge's definition at the moment its brick is
 * picked up (there is no giver to walk up to and nothing on the job board), and `noOutro` means the
 * tally comes straight up when the clock stops — mid-chase, the controls are never taken away.
 *
 * The police react as they always do: every blow, dent and knocked bin is the same crime it is any other
 * time, so a good run usually ends with the law on you. Offline only: online the mission system refuses to
 * start anything and the bricks aren't drawn.
 */
import * as THREE from 'three';
import type { GameContext } from '../core/context';
import { distance, type V2 } from '../core/math';
import type { System } from '../core/system';
import type { BookEntry } from '../book/bookScreen';
import type { MissionDef, RewardLine } from '../missions/types';
import type { MissionScript } from '../missions/runner';
import type { MapMarker } from '../ui/uiTypes';
import type { WeaponId } from '../weapons/weaponData';
import { PAVE_Y } from '../world/view/ground';
import { DISTRICTS, districtAt } from '../world/lowBorough/data';
import type { CityLayout } from '../world/types';
import { matchbookPos } from './matchbooks';
import { nodeId } from './roadLine';
import {
  COMBO_WINDOW,
  MAYHEM_CHALLENGES,
  MAYHEM_FAIL_STAMP,
  MayhemRun,
  TIER_PAY,
  bookRun,
  formatScore,
  mayhemHint,
  mayhemObjective,
  mayhemPayout,
  mayhemTag,
  tierName,
  tierThresholds,
  type MayhemChallenge,
  type MayhemKind,
} from './mayhem';
import { MayhemHud } from './mayhemHud';
import type { ActivitiesSave } from './state';

/** On foot within this many metres of the brick, and it's in your hand. */
export const BRICK_REACH = 1.5;
/** After a run the brick waits until you've walked this far off before it can be picked up again. */
export const BRICK_REARM = 40;
/** The nearest brick within this many metres catches the light (the top-down camera sees about 25 m). */
export const BRICK_GLINT = 22;
/** Untried bricks show on the minimap only within this distance (they're meant to be come across). */
export const BRICK_MAP_RANGE = 70;
/** A motor is "smashed up" at this fraction of its health (or burnt out). */
export const MOTOR_WRECKED = 0.45;
/** The Vanes' motors: claret, with the odd Vesper among the Regents. */
export const VANE_CLARET = 0x5a1424;
/** Marked men about at once (more walk in as they go down). */
const HEAVIES_AT_ONCE = 6;
/** Seconds after a run ends (any way) before the lent weapon goes back (the sheet is up by then). */

interface Spot {
  c: MayhemChallenge;
  def: MissionDef;
  pos: V2;
  alley: boolean;
  /** Nearest POI (the definition's nominal giver: the job board needs a real one). */
  poi: string;
  district: string;
}

/** The run in progress (one at a time). */
interface Session {
  spot: Spot;
  run: MayhemRun;
  /** Heavies: ped ids still marked. */
  marked: Set<number>;
  /** Heavies: ids already knocked down (never counted twice). */
  floored: Set<number>;
  /** Motors: mission vehicle keys still standing. */
  motors: string[];
  /** Clutter: props standing when the run began / knocked during it. */
  baseline: Set<number>;
  counted: Set<number>;
  /** Where the objective marker points (nearest target), refreshed a few times a second. */
  target: V2 | null;
  retarget: number;
  /** Heavies: a beat between spawns. */
  spawnIn: number;
  /** The stamp before this run (pay: "first time DIABOLICAL"). */
  prevTier: number;
  /** Last whole second announced in the final ten (tick sound). */
  lastTick: number;
}

declare module '../core/events' {
  interface GameEvents {
    /** A mayhem challenge ended (src/activities): what came of it (the radio's news desk reads it). */
    mayhemFinished: { id: string; title: string; kind: MayhemKind; district: string; outcome: 'completed' | 'failed'; tier: number; score: number; done: number; count: number };
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Mayhem challenges: the bricks, the record, the run in progress. */
    mayhemState(): Record<string, unknown>;
    /**
     * Dev / test: put the player on foot by a challenge's brick and start it (as picking it up does).
     * False when it can't start (online, a job running, unknown id).
     */
    mayhemStart(id: string): boolean;
  }
}

export function createMayhemSystem(): System {
  let ctx: GameContext;
  const spots: Spot[] = [];
  /** The definition offered to the mission system right now (only while a brick is being picked up). */
  let offering: MissionDef | null = null;
  let session: Session | null = null;
  /** Bricks waiting for the player to walk off before they can be picked up again. */
  const resting = new Set<string>();
  /** A weapon lent for the run: given back (taken away) once the run is over. */
  let lent: { weapon: WeaponId; had: boolean; prev: WeaponId } | null = null;
  let hud: MayhemHud | null = null;
  let brickMesh: THREE.InstancedMesh | null = null;
  let glint: THREE.Mesh | null = null;
  let markMesh: THREE.InstancedMesh | null = null;
  let bricksDirty = true;
  let bricksShown = true;
  let time = 0;
  const offs: (() => void)[] = [];

  const save = () => ctx.services.get('save');
  const rec = (): ActivitiesSave['mayhem'] => save().data.activities.mayhem;
  const ui = () => ctx.services.tryGet('ui');
  const offline = (): boolean => !ctx.services.tryGet('net')?.online && (ctx.services.tryGet('economy')?.authority ?? 'local') === 'local';
  const audio = () => ctx.services.tryGet('audio');

  // ------------------------------------------------------------------ the definitions

  function nearestPoi(layout: CityLayout, p: V2): string {
    let best = layout.pois[0]?.id ?? '';
    let bd = Infinity;
    for (const q of layout.pois) {
      const d = distance(q.pos, p);
      if (d < bd) {
        bd = d;
        best = q.id;
      }
    }
    return best;
  }

  function districtName(p: V2): string {
    const id = districtAt(p);
    return DISTRICTS.find((d) => d.id === id)?.name ?? 'the borough';
  }

  function buildSpots(): void {
    const layout = ctx.services.get('layout');
    for (const c of MAYHEM_CHALLENGES) {
      const pos = matchbookPos(layout, c);
      if (!pos || layout.pois.length === 0) continue;
      const a = nodeId(layout, c.a);
      const b = nodeId(layout, c.b);
      const e = layout.roads.edges.find((q) => (q.from === a && q.to === b) || (q.from === b && q.to === a));
      const spot: Spot = { c, def: null as unknown as MissionDef, pos, alley: !!e && (e.alley || e.sidewalk <= 0), poi: nearestPoi(layout, pos), district: districtName(pos) };
      spot.def = defFor(spot);
      spots.push(spot);
    }
  }

  function defFor(spot: Spot): MissionDef {
    const c = spot.c;
    const brick: V2 = { x: spot.pos.x, z: spot.pos.z };
    return {
      id: c.id,
      title: c.title,
      client: 'A note round a half-brick',
      tag: mayhemTag(c),
      blurb: c.note,
      reward: TIER_PAY[2],
      requires: [],
      repeatable: true,
      giver: { poi: spot.poi, prompt: 'Pick up the half-brick', glyph: 'H' },
      intro: [],
      outro: [],
      noOutro: true,
      activity: {
        kicker: `Half-brick · ${c.place}`,
        meta: () => (rec().best[c.id] ? `Best: ${formatScore(rec().best[c.id]!)} (${tierName(rec().tier[c.id] ?? 0).toUpperCase()})` : undefined),
        tried: () => rec().runs > 0,
      },
      setup: (m) => setup(m, spot),
      stages: [
        {
          id: 'mayhem',
          kind: 'custom',
          text: mayhemObjective(c),
          hint: (touch) => mayhemHint(c, touch),
          at: { fn: () => session?.target ?? brick },
          radius: 3,
          onEnter: (m) => begin(m, spot),
          onTick: (m, dt) => tick(m, dt),
          progress: () => (session ? session.run.done / c.count : 0),
          done: () => !!session && session.run.over && session.run.tier >= 1,
        },
      ],
      payout: (): RewardLine[] => (session ? mayhemPayout(session.run.tier, session.prevTier) : []),
      resultStamp: () => (session ? tierName(session.run.tier).toUpperCase() : 'TIDY'),
      resultLine: () => (session ? resultLine(session) : ''),
      failStamp: MAYHEM_FAIL_STAMP,
      onResolved: (_m, outcome) => resolve(outcome),
    };
  }

  function resultLine(s: Session): string {
    const r = s.run;
    const c = s.spot.c;
    const t = tierThresholds(c.count);
    const next = r.tier < 3 ? ` ${tierName(r.tier + 1).toUpperCase()} needs ${formatScore(t[r.tier]!)}.` : '';
    const time = r.done >= c.count ? ` with ${Math.floor(r.left)} s to spare (+${formatScore(r.timeBonus)})` : '';
    return `Score ${formatScore(r.score)}: ${r.done} of ${c.count}${time}, best combo ×${r.bestCombo}.${next}`;
  }

  // ------------------------------------------------------------------ a run

  /** Start (and every "start again"): the weapon or the car, the motors, the player by the brick. */
  function setup(m: MissionScript, spot: Spot): void {
    const c = spot.c;
    const player = ctx.services.get('player');
    // Back at the brick when it's a restart from across town (the sheet after NICKED / WASTED).
    if (!c.car && distance(player.pos, spot.pos) > 30) m.placePlayer({ pos: spot.pos });
    if (c.car) {
      m.spawnVehicle('motor', c.car, { pos: spot.pos });
      m.placePlayer({ pos: spot.pos }, 'motor');
    }
    if (c.weapon) lend(c.weapon, c.rounds);
    if (c.kind === 'motors') {
      const at = motorSpots(spot.pos, c.count);
      at.forEach((p, i) => m.spawnVehicle(`vane${i}`, i % 3 === 2 ? 'coupe' : i % 3 === 1 ? 'exec' : 'saloon', { pos: p }, { locked: true, parked: true, paint: VANE_CLARET }));
    }
  }

  /** Kerbside spots for the Vanes' motors: 12–90 m from the brick, at least 9 m apart, nearest first. */
  function motorSpots(from: V2, n: number): V2[] {
    const layout = ctx.services.get('layout');
    const cand = layout.parkedSpots
      .filter((s) => s.allowedTypes.length === 0)
      .map((s) => ({ p: s.pos, d: distance(s.pos, from) }))
      .filter((s) => s.d >= 12 && s.d <= 90)
      .sort((a, b) => a.d - b.d);
    const out: V2[] = [];
    for (const s of cand) {
      if (out.length >= n) break;
      if (out.every((o) => distance(o, s.p) >= 9)) out.push({ x: s.p.x, z: s.p.z });
    }
    return out;
  }

  function lend(weapon: WeaponId, rounds?: number): void {
    const w = ctx.services.tryGet('weapons');
    if (!w) return;
    const prev = w.current;
    const had = weapon === 'fists' || w.owned().includes(weapon);
    if (!lent) lent = { weapon, had, prev };
    if (weapon === 'fists') w.select('fists');
    else w.give(weapon, { ...(rounds ? { ammo: rounds } : {}), select: true });
  }

  function giveBack(): void {
    const l = lent;
    lent = null;
    const w = ctx.services.tryGet('weapons');
    if (!l || !w) return;
    if (!l.had) w.take(l.weapon);
    if (w.owned().includes(l.prev)) w.select(l.prev);
  }

  /** The stage begins (at the intro card): the run's record, the marked men, the baseline of standing props. */
  function begin(m: MissionScript, spot: Spot): void {
    const c = spot.c;
    const s: Session = {
      spot,
      run: new MayhemRun(c.count, c.seconds, COMBO_WINDOW[c.kind]),
      marked: new Set(),
      floored: new Set(),
      motors: [],
      baseline: new Set(),
      counted: new Set(),
      target: null,
      retarget: 0,
      spawnIn: 0,
      prevTier: rec().tier[c.id] ?? 0,
      lastTick: -1,
    };
    session = s;
    if (c.kind === 'motors') {
      for (let i = 0; i < c.count; i++) if (m.vehicle(`vane${i}`)) s.motors.push(`vane${i}`);
      m.listen('vehicleDamaged', (e) => {
        if (s.motors.some((k) => m.vehicle(k)?.id === e.vehicleId)) s.run.graze();
      });
    }
    if (c.kind === 'heavies') {
      m.listen('weaponHit', (e) => {
        if (e.target !== 'ped' || !s.marked.has(e.id)) return;
        if (!e.knocked && !e.killed) return s.run.graze();
        s.marked.delete(e.id);
        s.floored.add(e.id);
        scored(s);
      });
      for (let i = 0; i < Math.min(HEAVIES_AT_ONCE, c.count); i++) spawnHeavy(s, spot.pos, 8, 34);
    }
    if (c.kind === 'clutter') {
      for (const p of ctx.services.tryGet('props')?.all() ?? []) if (p.knocked) s.baseline.add(p.id);
      m.listen('propHit', (e) => {
        if (!e.byPlayer || s.baseline.has(e.propId) || s.counted.has(e.propId)) return;
        s.counted.add(e.propId);
        scored(s);
      });
    }
  }

  /** A marked man walks in somewhere between `min` and `max` m from `around`. */
  function spawnHeavy(s: Session, around: V2, min: number, max: number): void {
    const peds = ctx.services.tryGet('peds');
    if (!peds) return;
    const k = s.marked.size + s.floored.size;
    // Spread round the circle (golden angle), deterministic.
    const ang = k * 2.39996 + s.spot.c.count;
    const r = min + ((k * 7) % 10) / 10 * (max - min);
    const id = peds.spawnAt(around.x + Math.sin(ang) * r, around.z + Math.cos(ang) * r);
    if (id !== null) s.marked.add(id);
  }

  function scored(s: Session): void {
    const h = s.run.hit();
    if (!h) return;
    hud?.hit(h.points, h.combo);
    ctx.services.tryGet('cameraRig')?.shake(Math.min(0.35, 0.1 + h.combo * 0.04));
    const a = audio();
    a?.ui(h.combo > 1 ? 'stamp' : 'tick');
    if (h.tierUp) {
      a?.ui('cash');
      ctx.services.tryGet('cameraRig')?.shake(0.4);
      ui()?.toast(`Stamped ${tierName(h.tierUp).toUpperCase()} — ${formatScore(s.run.score)}`, 'good', 2.2);
    }
  }

  function tick(m: MissionScript, dt: number): void {
    const s = session;
    if (!s || s.run.over) return;
    const c = s.spot.c;
    s.run.tick(dt);
    if (c.kind === 'motors') {
      for (const key of s.motors.slice()) {
        const v = m.vehicle(key);
        if (v && !v.destroyed && v.health / v.spec.maxHealth > MOTOR_WRECKED) continue;
        s.motors.splice(s.motors.indexOf(key), 1);
        m.releaseVehicle(key);
        // Gone from the world entirely (despawned): no score, the job just has one fewer to find.
        if (v) scored(s);
      }
    }
    if (c.kind === 'heavies') heavies(s, dt);
    s.retarget -= dt;
    if (s.retarget <= 0) {
      s.retarget = 0.4;
      s.target = nearestTarget(m, s);
    }
    // The last ten seconds count down out loud.
    const sec = Math.ceil(s.run.left);
    if (s.run.left <= 10 && sec !== s.lastTick && sec > 0) {
      s.lastTick = sec;
      audio()?.ui('tick');
    }
    if (s.run.over && s.run.tier === 0) {
      const t = tierThresholds(c.count)[0]!;
      m.fail(`Time’s up: ${s.run.done} of ${c.count}, score ${formatScore(s.run.score)}. TIDY needs ${formatScore(t)}.`);
    }
  }

  function heavies(s: Session, dt: number): void {
    const peds = ctx.services.tryGet('peds');
    if (!peds) return;
    for (const id of [...s.marked]) {
      // Gone (walked off and released) or flattened by something else (a car): no longer a target.
      if (peds.stateOf(id) === null || peds.isDead(id)) s.marked.delete(id);
    }
    const remaining = s.spot.c.count - s.run.done;
    s.spawnIn -= dt;
    if (s.marked.size < Math.min(HEAVIES_AT_ONCE, remaining) && s.spawnIn <= 0) {
      s.spawnIn = 1.2;
      spawnHeavy(s, ctx.services.get('player').pos, 16, 32);
    }
  }

  const _near: V2 = { x: 0, z: 0 };
  function nearestTarget(m: MissionScript, s: Session): V2 | null {
    const me = ctx.services.get('player').pos;
    let best: V2 | null = null;
    let bd = Infinity;
    const consider = (x: number, z: number): void => {
      const d = Math.hypot(x - me.x, z - me.z);
      if (d < bd) {
        bd = d;
        best = { x, z };
      }
    };
    switch (s.spot.c.kind) {
      case 'motors':
        for (const k of s.motors) {
          const v = m.vehicle(k);
          if (v) consider(v.pos.x, v.pos.z);
        }
        break;
      case 'heavies':
        ctx.services.tryGet('peds')?.forEachNear(me.x, me.z, 120, (x, z, id) => {
          if (s.marked.has(id)) consider(x, z);
        });
        break;
      default:
        for (const p of ctx.services.tryGet('props')?.all() ?? []) {
          if (p.knocked || s.counted.has(p.id)) continue;
          _near.x = p.body.pos.x;
          _near.z = p.body.pos.z;
          if (Math.abs(_near.x - me.x) < 70 && Math.abs(_near.z - me.z) < 70) consider(_near.x, _near.z);
        }
    }
    return best;
  }

  /** The run is over (paid, failed, nicked or wasted): book it, say so, hand back what was lent. */
  function resolve(outcome: 'completed' | 'failed'): void {
    const s = session;
    if (!s) return;
    const c = s.spot.c;
    const r = s.run;
    const tier = outcome === 'completed' ? r.tier : 0;
    bookRun(rec(), c.id, outcome === 'completed' ? r.score : 0, tier);
    save().markDirty();
    resting.add(c.id);
    bricksDirty = true;
    ctx.events.emit('mayhemFinished', { id: c.id, title: c.title, kind: c.kind, district: s.spot.district, outcome, tier, score: r.score, done: r.done, count: c.count });
    s.marked.clear();
    giveBack();
    // Keep the session for the sheet's lines (resultLine / stamp are read after this); cleared on the next run.
  }

  /** Pick up a brick: offer its definition to the mission system for exactly this one call. */
  function pickUp(spot: Spot, force = false): boolean {
    const missions = ctx.services.get('missions');
    offering = spot.def;
    const ok = missions.start(spot.def.id, force);
    offering = null;
    if (ok) {
      bricksDirty = true;
      audio()?.ui('whoosh');
    }
    return ok;
  }

  function checkBricks(): void {
    if (!offline() || ctx.state.mode !== 'playing') return;
    const player = ctx.services.get('player');
    for (const s of spots) {
      const d = distance(player.pos, s.pos);
      if (resting.has(s.c.id)) {
        if (d > BRICK_REARM && ctx.state.activeMissionId !== s.c.id) {
          resting.delete(s.c.id);
          bricksDirty = true;
        }
        continue;
      }
      if (player.mode !== 'foot' || d > BRICK_REACH) continue;
      const u = ui();
      if (ctx.state.activeMissionId !== null || (u && ctx.uiRoot && !u.free)) continue;
      if (pickUp(s)) resting.add(s.c.id);
      return;
    }
  }

  // ------------------------------------------------------------------ the bricks, the marks, the slip

  function buildView(): void {
    if (!ctx.render || brickMesh) return;
    // A half-brick with a paper note tied round it: one draw call for all of them.
    const brick = new THREE.BoxGeometry(0.2, 0.065, 0.1);
    const mat = new THREE.MeshStandardMaterial({ color: 0x8e3b2a, roughness: 0.95, metalness: 0, emissive: 0x2a120c, emissiveIntensity: 0.5 });
    brickMesh = new THREE.InstancedMesh(brick, mat, spots.length * 2);
    brickMesh.name = 'mayhem-bricks';
    brickMesh.frustumCulled = false;
    brickMesh.castShadow = false;
    brickMesh.receiveShadow = true;
    ctx.scene.add(brickMesh);
    const g = new THREE.RingGeometry(0.28, 0.46, 20);
    g.rotateX(-Math.PI / 2);
    glint = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: 0xffd9a0, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
    glint.name = 'mayhem-glint';
    glint.visible = false;
    ctx.scene.add(glint);
    // The marked men: a small red chevron over the head.
    const cone = new THREE.ConeGeometry(0.16, 0.3, 4);
    cone.rotateX(Math.PI);
    markMesh = new THREE.InstancedMesh(cone, new THREE.MeshBasicMaterial({ color: 0xd03420 }), HEAVIES_AT_ONCE + 4);
    markMesh.name = 'mayhem-marks';
    markMesh.frustumCulled = false;
    markMesh.count = 0;
    ctx.scene.add(markMesh);
  }

  const _m = new THREE.Matrix4();
  const _q = new THREE.Quaternion();
  const _p = new THREE.Vector3();
  const _s = new THREE.Vector3(1, 1, 1);
  const _c = new THREE.Color();
  const _up = new THREE.Vector3(0, 1, 0);

  function syncView(): void {
    if (!brickMesh) return;
    const show = offline();
    if (show !== bricksShown) {
      bricksShown = show;
      bricksDirty = true;
    }
    if (bricksDirty) {
      bricksDirty = false;
      let n = 0;
      for (const s of spots) {
        if (!show || ctx.state.activeMissionId === s.c.id) continue;
        const k = spots.indexOf(s);
        const y = (s.alley ? 0 : PAVE_Y) + 0.033;
        _q.setFromAxisAngle(_up, k * 1.3);
        _m.compose(_p.set(s.pos.x, y, s.pos.z), _q, _s.set(1, 1, 1));
        brickMesh.setMatrixAt(n, _m);
        brickMesh.setColorAt(n, _c.setHex(0x8e3b2a));
        n++;
        // The note: a paper band round the middle.
        _m.compose(_p.set(s.pos.x, y + 0.002, s.pos.z), _q, _s.set(0.42, 1.12, 1.08));
        brickMesh.setMatrixAt(n, _m);
        brickMesh.setColorAt(n, _c.setHex(0xf2ead2));
        n++;
      }
      _s.set(1, 1, 1);
      brickMesh.count = n;
      brickMesh.instanceMatrix.needsUpdate = true;
      if (brickMesh.instanceColor) brickMesh.instanceColor.needsUpdate = true;
    }
    if (glint) {
      const p = ctx.services.get('player').pos;
      let best: Spot | null = null;
      let bd = BRICK_GLINT;
      if (show && ctx.state.activeMissionId === null) {
        for (const s of spots) {
          if (resting.has(s.c.id)) continue;
          const d = distance(p, s.pos);
          if (d < bd) {
            bd = d;
            best = s;
          }
        }
      }
      glint.visible = !!best;
      if (best) {
        const pulse = Math.max(0, Math.sin(time * 2.8)) ** 4;
        glint.position.set(best.pos.x, (best.alley ? 0 : PAVE_Y) + 0.04, best.pos.z);
        glint.scale.setScalar(1 + pulse * 1.3);
        (glint.material as THREE.MeshBasicMaterial).opacity = 0.25 + pulse * 0.7;
      }
    }
    if (markMesh) {
      let n = 0;
      const s = session;
      if (s && s.spot.c.kind === 'heavies' && !s.run.over && ctx.state.activeMissionId === s.spot.c.id) {
        const me = ctx.services.get('player').pos;
        const bob = Math.sin(time * 4) * 0.08;
        ctx.services.tryGet('peds')?.forEachNear(me.x, me.z, 60, (x, z, id) => {
          if (n >= markMesh!.instanceMatrix.count || !s.marked.has(id)) return;
          _q.setFromAxisAngle(_up, time * 2);
          _m.compose(_p.set(x, 2.25 + bob, z), _q, _s.set(1, 1, 1));
          markMesh!.setMatrixAt(n++, _m);
        });
      }
      markMesh.count = n;
      markMesh.instanceMatrix.needsUpdate = true;
    }
  }

  function hudFrame(): void {
    if (!hud) return;
    const s = session;
    const running = !!s && ctx.state.activeMissionId === s.spot.c.id && ctx.services.get('missions').stageKind === 'custom';
    if (!s || !running) return hud.set(null);
    const r = s.run;
    const t = tierThresholds(s.spot.c.count);
    hud.set({ title: s.spot.c.title, left: r.left, done: r.done, count: s.spot.c.count, score: r.score, combo: r.liveCombo, tier: r.tier, tierName: tierName(r.tier), next: r.tier < 3 ? t[r.tier]! : null });
  }

  function markers(out: MapMarker[]): void {
    if (!offline() || (ctx.state.mode !== 'playing' && ctx.state.mode !== 'paused')) return;
    const s = session;
    if (s && ctx.state.activeMissionId === s.spot.c.id) {
      if (s.spot.c.kind !== 'heavies') return;
      const me = ctx.services.get('player').pos;
      ctx.services.tryGet('peds')?.forEachNear(me.x, me.z, 120, (x, z, id) => {
        if (s.marked.has(id)) out.push({ x, z, kind: 'target', pin: false });
      });
      return;
    }
    if (ctx.state.activeMissionId !== null) return;
    const me = ctx.services.get('player').pos;
    for (const b of spots) {
      const tried = (rec().best[b.c.id] ?? 0) > 0 || (rec().tier[b.c.id] ?? 0) > 0;
      if (!tried && distance(me, b.pos) > BRICK_MAP_RANGE) continue;
      // Work on offer: a green H (the paper ! is the police station).
      out.push({ x: b.pos.x, z: b.pos.z, kind: 'friend', glyph: 'H', pin: false, label: `Half-brick — ${b.c.title}` });
    }
  }

  function jobsEntries(): BookEntry[] {
    if (!offline()) return [];
    const r = rec();
    if (r.runs === 0) return [];
    const tried = spots.filter((s) => r.best[s.c.id] || r.tier[s.c.id]);
    const top = spots.filter((s) => (r.tier[s.c.id] ?? 0) >= 3).length;
    return [
      {
        id: 'side:mayhem',
        title: `Half-bricks — ${tried.length} of ${spots.length} stamped`,
        sub: 'Two minutes to make a point',
        mark: top >= spots.length ? 'done' : 'new',
        kicker: 'Mayhem',
        heading: 'Half-bricks',
        body: [
          'Half-bricks with a note tied round them, left about the borough. Walk over one and the note says what to do: knock down the Vanes’ men, smash up their motors or clear the pavements, in two minutes, with what it leaves you.',
          'Every target scores; quick ones in a row score more. TIDY, PROPER or DIABOLICAL, and a bit of cash for the stamp. The police take it as they find it.',
          ...spots.filter((s) => r.best[s.c.id]).map((s) => `${s.c.title} (${s.c.place}): ${tierName(r.tier[s.c.id] ?? 0).toUpperCase()}, best ${formatScore(r.best[s.c.id]!)}.`),
        ],
        meta: `Played ${r.runs} time${r.runs === 1 ? '' : 's'}. ${top} stamped DIABOLICAL.`,
      },
    ];
  }

  // ------------------------------------------------------------------ system

  return {
    id: 'mayhem',
    order: 536,
    init(c) {
      ctx = c;
      buildSpots();
      c.services.get('missions').registerActivities(() => (offering ? [offering] : []));
      offs.push(
        c.events.on('newGameStarted', () => {
          session = null;
          resting.clear();
          lent = null;
          bricksDirty = true;
        }),
      );
      c.hooks.register('mayhemState', () => {
        const s = session;
        const r = rec();
        return {
          spots: spots.map((b) => ({ id: b.c.id, title: b.c.title, kind: b.c.kind, x: +b.pos.x.toFixed(2), z: +b.pos.z.toFixed(2), poi: b.poi, district: b.district, resting: resting.has(b.c.id) })),
          record: { best: { ...r.best }, tier: { ...r.tier }, runs: r.runs },
          lent: lent ? { ...lent } : null,
          run: s
            ? {
                id: s.spot.c.id,
                active: ctx.state.activeMissionId === s.spot.c.id,
                left: +s.run.left.toFixed(2),
                done: s.run.done,
                count: s.spot.c.count,
                score: s.run.score,
                combo: s.run.liveCombo,
                tier: s.run.tier,
                over: s.run.over,
                marked: [...s.marked],
                motors: s.motors.slice(),
                target: s.target ? { x: +s.target.x.toFixed(1), z: +s.target.z.toFixed(1) } : null,
              }
            : null,
          hud: hud && !hud.root.hidden,
        };
      });
      c.hooks.register('mayhemStart', (id) => {
        const s = spots.find((b) => b.c.id === id);
        if (!s || !offline() || ctx.state.activeMissionId !== null) return false;
        const player = c.services.get('player');
        if (player.mode !== 'foot') player.exitVehicle();
        player.teleport({ x: s.pos.x, z: s.pos.z });
        const ok = pickUp(s, true);
        if (ok) resting.add(s.c.id);
        return ok;
      });
      c.hooks.registerSnapshot('mayhem', () => ({ runs: rec().runs, active: session && ctx.state.activeMissionId === session.spot.c.id ? session.spot.c.id : null }));
    },
    start() {
      if (ctx.uiRoot) {
        const root = (ctx.uiRoot.querySelector('.bh-hud') ?? ctx.uiRoot.querySelector('.bh-ui')) as HTMLElement | null;
        if (root) hud = new MayhemHud(root);
      }
      buildView();
      offs.push(ctx.services.tryGet('book')?.addJobsSource(jobsEntries) ?? (() => undefined));
      const u = ui();
      if (u) offs.push(u.addMarkerSource(markers));
    },
    fixedUpdate() {
      checkBricks();
      // A run that ended without its resolve (aborted by another job or a new game): hand back the loan.
      if (lent && (!session || ctx.state.activeMissionId !== session.spot.c.id)) giveBack();
    },
    update(dt) {
      time += dt;
      syncView();
      hudFrame();
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      hud?.dispose();
      hud = null;
      for (const mesh of [brickMesh, glint, markMesh]) {
        if (!mesh) continue;
        ctx.scene.remove(mesh);
        mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
      }
      brickMesh = null;
      glint = null;
      markMesh = null;
    },
  };
}
